import React from 'react';
import posthog from 'posthog-js';
import { useAuthState } from './auth';
import { useOptionalFilterState } from '@/hooks/useFilter';
import { isPostHogInitialised } from './analytics/posthog';
import { buildAnalyticsIdentity } from './analytics/identity';

/**
 * Identifies the logged-in user (and their company) with PostHog. Modelled
 * on RegisterSentryUser, but the identify/group calls sit inside a
 * useEffect keyed on the resolved distinctId/groupKey — unlike
 * RegisterSentryUser, which calls a side-effecting API during render.
 *
 * One component serves both apps: in maquinista there is no filter context
 * (the atom stays at its `kind: 'none'` initial value, so this hook yields
 * `null` there) and the builder's non-demo-first-role fallback applies. See
 * docs/product-analytics-contract.md and identity.ts.
 *
 * In the manager app the selected company lives in `filterAtom`, which
 * `CompanyFilterContext` SCOPES (jotai-scope): a mount outside that provider
 * only ever sees the global atom, which the company selector never touches
 * (and which may still hold the company of an earlier route). AppManager
 * therefore mounts this component twice with different jobs:
 *
 * - `scope="global"`, outside the provider: identify only — the distinct id
 *   and the company-independent person properties (`app`, `app_version`,
 *   `has_multiple_companies`). Never groups, never writes `company_id`,
 *   so it can't race the scoped mount with a stale company.
 * - default (`scope="company"`), inside the provider: identify with the
 *   full allowlist (company props from the selection) + the group. The
 *   effect is keyed on groupKey, so every company switch re-calls
 *   posthog.group(...); identify() on the same distinct id is a `$set`, not
 *   a duplicate `$identify`. Leaving the company routes leaves the last
 *   group in place, which is the right attribution for what follows.
 *
 * Maquinista has no filter context (the atom stays at its `kind: 'none'`
 * initial value) and a single mount: the builder's non-demo-first-role
 * fallback applies.
 */
const COMPANY_PERSON_PROPERTIES = ['role', 'company_id', 'company_slug'];

export const RegisterPostHogUser = ({
  scope = 'company',
}: {
  scope?: 'global' | 'company';
}) => {
  const { user } = useAuthState();
  const currentCompanyId = useOptionalFilterState().value.company_id;

  const identity = buildAnalyticsIdentity(user, currentCompanyId);
  const distinctId = identity?.distinctId;
  const groupKey = scope === 'company' ? identity?.groupKey : undefined;
  // Same user, same company, but a property changed (role, company name or
  // slug, has_multiple_companies after accepting an invitation): the
  // identify/group calls must re-run so PostHog doesn't keep stale values
  // until the next sign-in. Serialised because the builder returns fresh
  // objects on every render.
  const propertiesKey = identity
    ? JSON.stringify([identity.personProperties, identity.groupProperties])
    : undefined;

  React.useEffect(() => {
    if (!isPostHogInitialised() || !identity) return;

    try {
      if (scope === 'global') {
        const globalProperties = Object.fromEntries(
          Object.entries(identity.personProperties).filter(
            ([key]) => !COMPANY_PERSON_PROPERTIES.includes(key)
          )
        );
        posthog.identify(identity.distinctId, globalProperties);
        return;
      }
      posthog.identify(identity.distinctId, identity.personProperties);
      if (identity.groupKey) {
        posthog.group(
          identity.groupType,
          identity.groupKey,
          identity.groupProperties
        );
      } else {
        // No company role (any more): identify() only merges, so the
        // company person properties from an earlier role and the group
        // posthog-js persists in localStorage would otherwise stay
        // attached to everything that follows. Unset both explicitly.
        posthog.unsetPersonProperties(COMPANY_PERSON_PROPERTIES);
        posthog.resetGroups();
      }
    } catch (error) {
      console.error('Failed to identify PostHog user', error);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [distinctId, groupKey, scope, propertiesKey]);

  return null;
};
